'use client';

import { c as _c } from "react/compiler-runtime";
import { useReducedMotion } from 'motion/react';
import { type RefObject, useEffect } from 'react';
import { useMediaQuery } from '@/app/hooks/useMediaQuery';
import { createPointerPosition, usePointerPosition } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/usePointerPosition';
import { useMutableRef } from '@/app/lcv/components/sections/spartan/shared/webgpu/hooks/useMutableRef';

// HOW FAR THE ENGINE LEANS, as a percentage of its OWN box, with the
// cursor at the very edge of the slide.
const PARALLAX_X_PERCENT = 1.2;
const PARALLAX_Y_PERCENT = 0.8;

// Time constant of the ease toward the pointer. Larger is heavier: the
// engine trails the cursor instead of being pinned to it, which is the
// difference between a heavy object and a sticker stuck to the mouse.
const PARALLAX_RESPONSE_SECONDS = 0.45;

// Frame deltas are clamped before easing. A backgrounded tab hands back
// one enormous delta on its first frame back, which would otherwise
// resolve the whole ease at once and snap the engine across.
const MAX_FRAME_SECONDS = 1 / 30;

// Below this a write would move the engine by a fraction of a pixel.
// Skipping it is what lets a resting cursor leave the engine's layer
// completely alone, instead of committing a new transform every frame for
// as long as the slide is open.
const REST_EPSILON_PERCENT = 0.001;
interface UseEnginePointerParallaxOptions {
  // WHERE THE POINTER IS MEASURED -- the whole slide, not the engine's own
  // box. Measured against the engine, the lean would only exist while the
  // cursor was over the artwork itself and would jump between its
  // extremes as the cursor crossed the edge.
  areaRef: RefObject<HTMLElement | null>;
  // The element the lean is written to. NOT the slot useEngineAnchor
  // measures, and NOT the element carrying the settle tween -- see below.
  elementRef: RefObject<HTMLElement | null>;
  // The engine having SETTLED, in practice. Starting earlier would add
  // drift to the hand-off tween that is still flying the engine in.
  enabled: boolean;
}
export const useEnginePointerParallax = t0 => {
  const $ = _c(9);
  const {
    areaRef,
    elementRef,
    enabled
  } = t0;
  const isDesktopPointer = useMediaQuery("(hover: hover) and (pointer: fine)") ?? false;
  const isReducedMotion = useReducedMotion();
  const isActive = enabled && isDesktopPointer && !isReducedMotion;
  const pointerRef = useMutableRef(createPointerPosition);
  let t1;
  if ($[0] !== areaRef || $[1] !== isActive || $[2] !== pointerRef) {
    t1 = {
      elementRef: areaRef,
      targetRef: pointerRef,
      enabled: isActive
    };
    $[0] = areaRef;
    $[1] = isActive;
    $[2] = pointerRef;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  usePointerPosition(t1);
  let t2;
  let t3;
  if ($[4] !== elementRef || $[5] !== isActive || $[6] !== pointerRef) {
    t2 = () => {
      const element = elementRef.current;
      if (!element || !isActive) {
        return;
      }
      let frame = 0;
      let last = performance.now();
      let x = 0;
      let y = 0;
      let writtenX = 0;
      let writtenY = 0;
      const tick = now => {
        frame = requestAnimationFrame(tick);
        const delta = Math.min((now - last) / 1000, MAX_FRAME_SECONDS);
        last = now;
        const pointer = pointerRef.value;
        const targetX = pointer.isInside ? (pointer.x - 0.5) * 2 * PARALLAX_X_PERCENT : 0;
        const targetY = pointer.isInside ? -(pointer.y - 0.5) * 2 * PARALLAX_Y_PERCENT : 0;
        const ease = 1 - Math.exp(-delta / PARALLAX_RESPONSE_SECONDS);
        x = x + (targetX - x) * ease;
        x;
        y = y + (targetY - y) * ease;
        y;
        if (Math.abs(x - writtenX) < REST_EPSILON_PERCENT && Math.abs(y - writtenY) < REST_EPSILON_PERCENT) {
          return;
        }
        writtenX = x;
        writtenY = y;
        element.style.translate = `${x}% ${y}%`;
      };
      element.style.willChange = "translate";
      frame = requestAnimationFrame(tick);
      return () => {
        cancelAnimationFrame(frame);
        element.style.translate = "";
        element.style.willChange = "";
      };
    };
    t3 = [elementRef, pointerRef, isActive];
    $[4] = elementRef;
    $[5] = isActive;
    $[6] = pointerRef;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  useEffect(t2, t3);
};